import { useEffect } from "react";
import { motion } from "framer-motion";
import { HelpCircle, ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { SubpageShell } from "@/components/layout/SubpageShell";
import { FAQ_META, FAQ_SECTIONS as DEFAULT_SECTIONS, FAQ_CLOSING as DEFAULT_CLOSING, FAQ_DISCLAIMER as DEFAULT_DISCLAIMER } from "@/data/faqContent";

const EASE = [0.22, 1, 0.36, 1];

const Block = ({ block, k }) => {
  if (block.t === "h3") {
    return <h3 className="pt-4 font-display font-bold text-lg sm:text-xl text-slate-900">{block[k]}</h3>;
  }
  if (block.t === "list") {
    return (
      <ul className="grid sm:grid-cols-2 gap-2.5">
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 rounded-xl bg-sky-50/70 border border-sky-100 px-4 py-3 text-sm font-medium text-slate-700">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
            {item[k]}
          </li>
        ))}
      </ul>
    );
  }
  if (block.t === "qa") {
    return (
      <div className="space-y-4">
        {block.items.map((qa, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="font-display font-bold text-base text-slate-900 flex items-start gap-2">
              <HelpCircle size={17} className="text-sky-500 mt-0.5 shrink-0" />
              {qa.q[k]}
            </p>
            <p className="mt-2 pl-6 text-sm leading-relaxed text-slate-600">{qa.a[k]}</p>
          </div>
        ))}
      </div>
    );
  }
  return <p className="text-base leading-relaxed text-slate-600">{block[k]}</p>;
};

export default function FaqPage() {
  const { lang, content } = useSite();
  const k = lang === "zh" ? "zh" : "en";
  const faq = content?.faq || {};
  const FAQ_SECTIONS = faq.sections?.length ? faq.sections : DEFAULT_SECTIONS;
  const FAQ_CLOSING = { ...DEFAULT_CLOSING, ...(faq.closing || {}) };
  const FAQ_DISCLAIMER = { ...DEFAULT_DISCLAIMER, ...(faq.disclaimer || {}) };

  useEffect(() => {
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", lang === "zh" ? FAQ_META.desc_zh : FAQ_META.desc_en);
  }, [lang]);

  return (
    <SubpageShell titleZh={FAQ_META.title_zh} titleEn={FAQ_META.title_en}>
      <div data-testid="faq-page" className="relative max-w-4xl mx-auto px-6 lg:px-12 pt-20 pb-28">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">FAQ</p>
          <h1 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-slate-900">
            {k === "zh" ? "什么是 i.S.i Group？" : "What Is i.S.i Group?"}
          </h1>
          <p className="mt-3 font-mono2 text-sm text-slate-400">Infinite Sanguine Innovators</p>
        </motion.div>

        <div className="mt-16 space-y-16">
          {FAQ_SECTIONS.map((s, i) => (
            <motion.section
              key={i}
              data-testid={`faq-section-${i}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <div className="flex gap-5 sm:gap-8 items-start">
                <span className="font-mono2 font-medium text-4xl sm:text-5xl text-sky-200 leading-none select-none shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-900">{s.title[k]}</h2>
                  <div className="mt-5 space-y-4">
                    {s.blocks.map((b, bi) => (
                      <Block key={bi} block={b} k={k} />
                    ))}
                  </div>
                </div>
              </div>
            </motion.section>
          ))}
        </div>

        <motion.div
          data-testid="faq-closing"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-24 rounded-3xl bg-slate-900 p-10 sm:p-14 text-center relative overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-sky-500/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-orange-500/10 blur-3xl" />
          <h2 className="relative font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white">
            {lang === "zh" ? FAQ_CLOSING.h_zh : FAQ_CLOSING.h_en}
          </h2>
          <p className="relative mt-6 text-slate-300">{lang === "zh" ? FAQ_CLOSING.p1_zh : FAQ_CLOSING.p1_en}</p>
          <p className="relative mt-3 font-display font-bold text-lg sm:text-xl text-orange-400">
            {lang === "zh" ? FAQ_CLOSING.p2_zh : FAQ_CLOSING.p2_en}
          </p>
          <p className="relative mt-6 text-slate-300">{lang === "zh" ? FAQ_CLOSING.p3_zh : FAQ_CLOSING.p3_en}</p>
          <p className="relative mt-8 font-display font-extrabold text-xl sm:text-2xl text-sky-400">{FAQ_CLOSING.motto_en}</p>
          <p className="relative mt-4 font-display font-bold text-white">{FAQ_CLOSING.brand}</p>
          <p className="relative text-xs text-slate-400 tracking-wide">{FAQ_CLOSING.brandSub}</p>
          <a
            data-testid="faq-contact-cta"
            href="/contact"
            className="relative mt-10 inline-flex items-center gap-2 rounded-full bg-orange-500 px-8 py-4 text-sm font-bold text-white hover:bg-orange-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            {lang === "zh" ? "联系 i.S.i Group" : "Contact i.S.i Group"}
            <ArrowRight size={16} />
          </a>
        </motion.div>

        <div data-testid="faq-disclaimer" className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{FAQ_DISCLAIMER.h}</p>
          <p className="mt-3 text-xs leading-relaxed text-slate-500">{FAQ_DISCLAIMER.en}</p>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">{FAQ_DISCLAIMER.zh}</p>
        </div>
      </div>
    </SubpageShell>
  );
}
